import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import ConflictCheck from "./components/ConflictCheck";
import { AuthProvider } from "./components/AuthGate";
import DocGenerator from "./components/DocGenerator";
import LegalSearch from "./components/LegalSearch";
import CaseDetailView from "./components/CaseDetailView";
import PrivacyPolicyModal from "./components/PrivacyPolicyModal";
import TermsOfServiceModal from "./components/TermsOfServiceModal";
import DeadlineCalculator from "./components/DeadlineCalculator";
import InvoiceCreator from "./components/InvoiceCreator";
import AIInsights from "./components/AIInsights";
import Syntaxi from './components/Syntaxi';
import './components/Syntaxi.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { 
  Users, FileText, Calendar, BarChart3, Plus, Search, Clock, 
  AlertTriangle, CheckCircle, DollarSign, Bell, Archive,
  Mail, Phone, MapPin, Edit, Trash2, Eye, Download,
  TrendingUp, Target, Zap, Brain, Shield
, Link as LinkIcon} from "lucide-react";
import AADESettings from "./AADESettings";

const CRMProfessional = ({ user, onLogout, isLoggedIn } = {}) => {
  // State Management
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || "dashboard";
  });
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.history.pushState({tab}, '', '#' + tab);
  };
  
  useEffect(() => {
    const onPopState = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) setActiveTab(hash);
      else window.location.href = '/';
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [aadeSettings, setAadeSettings] = useState(null);
  const [showAADESettings, setShowAADESettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDeadlineCalc, setShowDeadlineCalc] = useState(false);
  const [showInvoiceCreator, setShowInvoiceCreator] = useState(false);
  const [newClient, setNewClient] = useState({
    type: 'person', firstName: '', lastName: '', afm: '', email: '', phone: '',
    companyName: '', legalForm: '', gemi: '', representative: '', doy: '',
    street: '', streetNum: '', zip: '', city: '', region: '',
    idNumber: '', birthDate: '', profession: '', notes: '', referralSource: '',
    legalCategory: '', opponentName: '', opponentAfm: '', photo: null,
  });

  // Load data - empty for logged in users, demo for visitors
  useEffect(() => {
    if (!isLoggedIn) loadDemoData();
  }, []);

  const loadDemoData = () => {
    // Demo Clients
    setClients([
      { 
        id: 1, 
        name: "Γεώργιος Παπαδόπουλος", 
        email: "g.papadopoulos@example.com", 
        phone: "+30 210 1234567",
        company: "ΑΕ Παπαδόπουλος",
        taxId: "123456789",
        address: "Σταδίου 15, Αθήνα 10562",
        activeCases: 3,
        totalBilled: 15000,
        status: "active",
        avatar: "GP"
      },
      { 
        id: 2, 
        name: "Μαρία Κωνσταντίνου", 
        email: "m.konstantinou@example.com", 
        phone: "+30 210 2345678",
        company: "Freelancer",
        taxId: "987654321",
        address: "Πανεπιστημίου 42, Αθήνα 10679",
        activeCases: 1,
        totalBilled: 8500,
        status: "active",
        avatar: "MK"
      },
      { 
        id: 3, 
        name: "Δημήτρης Νικολάου", 
        email: "d.nikolaou@example.com", 
        phone: "+30 210 3456789",
        company: "ΕΠΕ Νικολάου",
        taxId: "456789123",
        address: "Ακαδημίας 28, Αθήνα 10671",
        activeCases: 2,
        totalBilled: 12000,
        status: "active",
        avatar: "DN"
      }
    ]);

    // Demo Cases
    setCases([
      { 
        id: 1, 
        clientId: 1,
        clientName: "Γεώργιος Παπαδόπουλος", 
        caseNumber: "2025-001", 
        type: "civil",
        typeLabel: "Αστικό",
        title: "Διαφορά από σύμβαση μίσθωσης",
        court: "Πρωτοδικείο Αθηνών",
        courtFileNumber: "12345/2025",
        status: "active",
        statusLabel: "Ενεργή",
        openedDate: "2025-01-15",
        nextHearing: "2025-03-20",
        estimatedValue: 50000,
        priority: "high",
        aiSuccessRate: 78,
        description: "Διεκδίκηση ανείσπρακτων μισθωμάτων και αποζημίωσης"
      },
      { 
        id: 2, 
        clientId: 2,
        clientName: "Μαρία Κωνσταντίνου", 
        caseNumber: "2025-002", 
        type: "labor",
        typeLabel: "Εργατικό",
        title: "Αναγνώριση εργασιακής σχέσης",
        court: "Πρωτοδικείο Αθηνών - Τμήμα Εργατικών",
        courtFileNumber: "54321/2025",
        status: "active",
        statusLabel: "Ενεργή",
        openedDate: "2025-02-01",
        nextHearing: "2025-04-15",
        estimatedValue: 25000,
        priority: "medium",
        aiSuccessRate: 85,
        description: "Αναγνώριση εξαρτημένης εργασίας και αποζημίωση"
      },
      { 
        id: 3, 
        clientId: 3,
        clientName: "Δημήτρης Νικολάου", 
        caseNumber: "2025-003", 
        type: "commercial",
        typeLabel: "Εμπορικό",
        title: "Ακύρωση απόφασης ΓΣ",
        court: "Μονομελές Πρωτοδικείο Αθηνών",
        courtFileNumber: "98765/2025",
        status: "pending",
        statusLabel: "Εκκρεμής",
        openedDate: "2024-11-20",
        nextHearing: "2025-02-28",
        estimatedValue: 100000,
        priority: "high",
        aiSuccessRate: 62,
        description: "Προσβολή απόφασης ΓΣ για αύξηση μετοχικού κεφαλαίου"
      }
    ]);

    // Demo Deadlines
    setDeadlines([
      {
        id: 1,
        caseId: 1,
        caseName: "2025-001 - Διαφορά μίσθωσης",
        title: "Κατάθεση προτάσεων",
        dueDate: "2025-02-20",
        type: "court",
        priority: "high",
        daysLeft: 9,
        aiCalculated: true,
        description: "Προθεσμία ΚΠολΔ 237 - 100 ημέρες από επίδοση"
      },
      {
        id: 2,
        caseId: 2,
        caseName: "2025-002 - Εργατικό",
        title: "Απάντηση σε ένσταση",
        dueDate: "2025-02-15",
        type: "court",
        priority: "critical",
        daysLeft: 4,
        aiCalculated: true,
        description: "Προθεσμία ΚΠολΔ 242 - 30 ημέρες"
      },
      {
        id: 3,
        caseId: 3,
        caseName: "2025-003 - Εμπορικό",
        title: "Ανταπάντηση",
        dueDate: "2025-02-25",
        type: "court",
        priority: "medium",
        daysLeft: 14,
        aiCalculated: true,
        description: "Προθεσμία ΚΠολΔ 242"
      }
    ]);

    // Demo Time Entries
    setTimeEntries([
      {
        id: 1,
        caseId: 1,
        caseName: "2025-001",
        date: "2025-02-10",
        hours: 2.5,
        description: "Σύνταξη αγωγής",
        hourlyRate: 150,
        amount: 375,
        billable: true
      },
      {
        id: 2,
        caseId: 1,
        caseName: "2025-001",
        date: "2025-02-09",
        hours: 1.0,
        description: "Συνάντηση με πελάτη",
        hourlyRate: 150,
        amount: 150,
        billable: true
      },
      {
        id: 3,
        caseId: 2,
        caseName: "2025-002",
        date: "2025-02-08",
        hours: 3.0,
        description: "Νομική έρευνα",
        hourlyRate: 150,
        amount: 450,
        billable: true
      }
    ]);

    // Demo Invoices
    setInvoices([
      {
        id: 1,
        clientId: 1,
        clientName: "Γεώργιος Παπαδόπουλος",
        invoiceNumber: "INV-2025-001",
        date: "2025-01-31",
        dueDate: "2025-02-28",
        amount: 3500,
        status: "paid",
        items: [
          { description: "Νομική Συμβουλευτική - Ιανουάριος", hours: 20, rate: 150, amount: 3000 },
          { description: "Έξοδα δικαστηρίου", hours: 0, rate: 0, amount: 500 }
        ]
      },
      {
        id: 2,
        clientId: 2,
        clientName: "Μαρία Κωνσταντίνου",
        invoiceNumber: "INV-2025-002",
        date: "2025-02-05",
        dueDate: "2025-03-05",
        amount: 2250,
        status: "pending",
        items: [
          { description: "Σύνταξη αγωγής", hours: 15, rate: 150, amount: 2250 }
        ]
      }
    ]);

    // AI Insights
    setAiInsights([
      {
        id: 1,
        type: "success_prediction",
        icon: "🎯",
        title: "Υψηλή πιθανότητα επιτυχίας",
        description: "Η υπόθεση 2025-002 έχει 85% πιθανότητα επιτυχίας βάσει παρόμοιων υποθέσεων",
        caseId: 2,
        priority: "info"
      },
      {
        id: 2,
        type: "deadline_alert",
        icon: "⚠️",
        title: "Κρίσιμη προθεσμία",
        description: "Απάντηση σε ένσταση λήγει σε 4 ημέρες (2025-002)",
        caseId: 2,
        priority: "critical"
      },
      {
        id: 3,
        type: "time_recommendation",
        icon: "⏱️",
        title: "Σύσταση χρέωσης",
        description: "Έχετε 12.5 μη τιμολογημένες ώρες αυτό το μήνα",
        priority: "warning"
      },
      {
        id: 4,
        type: "similar_cases",
        icon: "📚",
        title: "Παρόμοιες υποθέσεις",
        description: "Βρέθηκαν 47 παρόμοιες αποφάσεις για την υπόθεση 2025-001",
        caseId: 1,
        priority: "info"
      }
    ]);
  };

  // Stats Calculations
  const stats = {
    totalClients: clients.length,
    activeCases: cases.filter(c => c.status === 'active').length,
    upcomingDeadlines: deadlines.filter(d => d.daysLeft <= 7).length,
    unbilledHours: timeEntries.filter(t => t.billable && !t.invoiced).reduce((sum, t) => sum + t.hours, 0),
    pendingInvoices: invoices.filter(i => i.status === 'pending').length,
    totalRevenue: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0),
    monthlyRevenue: invoices.filter(i => i.status === 'paid' && i.date.startsWith('2025-02')).reduce((sum, i) => sum + i.amount, 0)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {showInvoiceCreator && <InvoiceCreator onClose={() => setShowInvoiceCreator(false)} />}
      {showDeadlineCalc && <DeadlineCalculator onClose={() => setShowDeadlineCalc(false)} />}
      {selectedCase && <CaseDetailView caseItem={selectedCase} onClose={() => setSelectedCase(null)} />}
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" 
                alt="ΝΟΜΟΚΡΑΤΙΑ" 
                className="h-10 w-auto cursor-pointer hover:opacity-70"
                onClick={() => window.location.href = "/"}
              />
              <div>
                <h1 onClick={() => window.location.href = "/"} className="text-xl font-bold text-gray-900 cursor-pointer hover:opacity-70" title="Αρχική">ΝΟΜΟΚΡΑΤΙΑ CRM</h1>
                <p className="text-xs text-gray-500">Νομικό Σύστημα Διαχείρισης • 3.5M Έγγραφα</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Badge className="bg-slate-900">
                <Brain className="h-3 w-3 mr-1" />
                AI Active
              </Badge>
              <Button size="sm" variant="outline">
                <Bell className="h-4 w-4 mr-2" />
                3
              </Button>
              {isLoggedIn ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">{user?.name || user?.email || ""}</span>
                  <Button onClick={() => { localStorage.removeItem("nomokratia_token"); localStorage.removeItem("nomokratia_user"); window.location.href = "/"; }} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                    Αποσύνδεση
                  </Button>
                </div>
              ) : (
                <Button onClick={() => window.location.href = "/"} variant="outline" size="sm">
                  Επιστροφή
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {isLoggedIn ? (
        <div className="bg-emerald-600 text-white py-3">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm font-medium">
              ⏰ <strong>14 ημέρες δωρεάν δοκιμή</strong> — Απολαύστε πλήρη πρόσβαση σε όλα τα εργαλεία
              <Button onClick={() => window.location.href = "/pricing"} size="sm" variant="outline" className="ml-4 text-white border-white hover:bg-white hover:text-emerald-700">
                Αγορά Πλήρους Έκδοσης
              </Button>
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-slate-900 text-white py-3">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm font-medium">
              🚀 <strong>DEMO MODE</strong> - Αυτά είναι δείγματα δεδομένων με AI Features.
              <Button onClick={() => window.location.href = "/?register=true"} size="sm" variant="outline" className="ml-4 text-white border-white hover:bg-white hover:text-blue-600">
                Εγγραφή για Πλήρη Πρόσβαση
              </Button>
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-12 crm-tabs">
            <TabsTrigger value="dashboard">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="clients">
              <Users className="h-4 w-4 mr-2" />
              Πελάτες
            </TabsTrigger>
            <TabsTrigger value="cases">
              <FileText className="h-4 w-4 mr-2" />
              Υποθέσεις
            </TabsTrigger>
            <TabsTrigger value="deadlines">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Προθεσμίες
            </TabsTrigger>
            <TabsTrigger value="time">
              <Clock className="h-4 w-4 mr-2" />
              Ώρες
            </TabsTrigger>
            <TabsTrigger value="invoices">
              <DollarSign className="h-4 w-4 mr-2" />
              Τιμολόγια
            </TabsTrigger>
            <TabsTrigger value="aade">
              <LinkIcon className="h-4 w-4 mr-2" />
              AADE
            </TabsTrigger>
            <TabsTrigger value="search">Νομολογία</TabsTrigger>
            <TabsTrigger value="documents">Έγγραφα</TabsTrigger>
            <TabsTrigger value="conflicts">Σύγκρουση</TabsTrigger>
            <TabsTrigger value="syntaxi">✍️ Σύνταξη</TabsTrigger>
            <TabsTrigger value="ai">
              <Brain className="h-4 w-4 mr-2" />
              AI Insights
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* AI Insights Banner */}
            <div className="grid md:grid-cols-2 gap-4">
              {aiInsights.slice(0, 2).map(insight => (
                <Alert 
                  key={insight.id}
                  className={`${
                    insight.priority === 'critical' ? 'border-red-500 bg-red-50' :
                    insight.priority === 'warning' ? 'border-yellow-500 bg-yellow-50' :
                    'border-blue-500 bg-blue-50'
                  }`}
                >
                  <span className="text-2xl mr-3">{insight.icon}</span>
                  <div>
                    <h4 className="font-bold text-sm mb-1">{insight.title}</h4>
                    <AlertDescription className="text-xs">{insight.description}</AlertDescription>
                  </div>
                </Alert>
              ))}
            </div>

            {/* Stats Grid */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-600" />
                    Ενεργοί Πελάτες
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stats.totalClients}</div>
                  <p className="text-xs text-gray-500 mt-1">+2 αυτό το μήνα</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-green-600" />
                    Ενεργές Υποθέσεις
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stats.activeCases}</div>
                  <p className="text-xs text-gray-500 mt-1">Μέση επιτυχία: 75%</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                    Κρίσιμες Προθεσμίες
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stats.upcomingDeadlines}</div>
                  <p className="text-xs text-gray-500 mt-1">Επόμενες 7 ημέρες</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-slate-700" />
                    Έσοδα Μήνα
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">€{stats.monthlyRevenue.toLocaleString()}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    <TrendingUp className="h-3 w-3 inline mr-1" />
                    +15% vs πρ. μήνα
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions + Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Zap className="h-5 w-5 mr-2 text-yellow-600" />
                    Γρήγορες Ενέργειες
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button className="w-full justify-start" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Νέος Πελάτης
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Νέα Υπόθεση
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Clock className="h-4 w-4 mr-2" />
                    Καταγραφή Ωρών
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Search className="h-4 w-4 mr-2" />
                    Αναζήτηση Νομολογίας
                  </Button>
                </CardContent>
              </Card>

              {/* Upcoming Deadlines */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-orange-600" />
                    Προσεχείς Προθεσμίες
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {deadlines.slice(0, 3).map(deadline => (
                    <div key={deadline.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{deadline.title}</p>
                        <p className="text-xs text-gray-600">{deadline.caseName}</p>
                      </div>
                      <Badge className={
                        deadline.priority === 'critical' ? 'bg-red-500' :
                        deadline.priority === 'high' ? 'bg-orange-500' :
                        'bg-yellow-500'
                      }>
                        {deadline.daysLeft} ημέρες
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CLIENTS TAB - Will continue in Part 2 */}
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Διαχείριση Πελατών</CardTitle>
                    <CardDescription>Όλοι οι πελάτες σας με AI analytics</CardDescription>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέος Πελάτης
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input placeholder="Αναζήτηση πελάτη..." className="max-w-md" />
                </div>
                
                <div className="space-y-4">
                  {clients.map(client => (
                    <div key={client.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-16 w-16 rounded-full bg-slate-900 flex items-center justify-center text-white font-bold text-xl">
                            {client.avatar}
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{client.name}</h3>
                            <p className="text-sm text-gray-600">{client.company}</p>
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                              <span>{client.email}</span>
                              <span>{client.phone}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              {client.email && (
                                <button onClick={(e) => {e.stopPropagation(); window.open('mailto:' + client.email);}}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                                  title="Αποστολή Email">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
                                  Email
                                </button>
                              )}
                              {client.phone && (
                                <button onClick={(e) => {e.stopPropagation(); window.open('https://wa.me/' + client.phone.replace(/[^0-9]/g,'').replace(/^0030/,'30').replace(/^6/,'306'));}}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
                                  title="WhatsApp">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                  WhatsApp
                                </button>
                              )}
                              {client.phone && (
                                <button onClick={(e) => {e.stopPropagation(); window.open('viber://chat?number=' + client.phone.replace(/[^0-9]/g,'').replace(/^0030/,'30').replace(/^6/,'306'));}}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                                  title="Viber">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#7360F2"><path d="M11.4 0C9.473.028 5.333.344 3.027 2.467 1.2 4.24.535 6.933.425 10.24.316 13.546.09 19.806 6.159 21.381v2.296s-.037.927.576 1.116c.744.228 1.18-.479 1.892-1.249.39-.422.927-1.044 1.333-1.52 3.667.311 6.487-.396 6.813-.506.75-.254 4.994-.789 5.684-6.44.712-5.832-.337-9.525-2.223-11.194C18.854.992 14.216-.04 11.4 0zm.587 1.658c2.524.027 6.408.744 7.838 1.96 1.561 1.385 2.387 4.624 1.77 9.618-.558 4.584-4.009 5.193-4.623 5.402-.272.092-2.694.697-5.786.485 0 0-2.291 2.765-3.007 3.484-.112.113-.244.157-.332.136-.125-.03-.16-.175-.158-.388.003-.28.018-3.527.018-3.527C4.021 17.354 4.22 12.076 4.31 9.29c.09-2.786.636-5.057 2.156-6.543C7.935 1.325 9.462.634 11.987 1.659z"/><path d="M11.873 4.54c-.204 0-.204.317 0 .32 1.312.02 2.492.474 3.39 1.308.9.836 1.43 2.01 1.476 3.322.006.204.32.199.315-.005-.05-1.42-.626-2.696-1.607-3.607-.982-.913-2.277-1.415-3.574-1.437zm.081 1.706c-.204-.003-.207.313-.003.32.855.048 1.655.454 2.222 1.105.57.654.86 1.471.836 2.328-.005.204.31.21.315.006.026-.952-.293-1.866-.924-2.59-.63-.723-1.5-1.122-2.446-1.17zm-.057 1.752c-.204-.01-.215.306-.012.32.485.064.93.322 1.222.734.294.414.394.908.306 1.387-.038.203.274.29.312.087.106-.58-.008-1.184-.365-1.688-.358-.504-.876-.808-1.463-.84zm-1.849 1.285c-.32-.003-.657.088-1.04.318l-.008.005c-.337.216-.723.677-.853 1.132-.2.705.01 1.517.787 2.498l.017.02c1.308 1.614 2.932 2.87 4.706 3.636l.025.01c.587.234 1.376.571 2.198.42.477-.087.957-.38 1.214-.815.136-.229.2-.48.16-.705-.06-.34-.36-.616-.635-.818l-.002-.002c-.473-.343-.987-.636-1.34-.838-.455-.26-.932-.04-1.112.217l-.437.542c-.204.244-.566.2-.566.2-2.691-.674-3.416-3.424-3.416-3.424s-.044-.362.2-.566l.542-.437c.257-.18.477-.657.217-1.112-.202-.353-.495-.867-.838-1.34l-.002-.002c-.202-.275-.478-.575-.818-.635-.1-.018-.2-.025-.3-.024z"/></svg>
                                  Viber
                                </button>
                              )}
                              {client.phone && (
                                <button onClick={(e) => {e.stopPropagation(); window.open('https://t.me/+' + client.phone.replace(/[^0-9]/g,'').replace(/^0030/,'30').replace(/^6/,'306'));}}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 transition-colors"
                                  title="Telegram">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#229ED9"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                                  Telegram
                                </button>
                              )}
                              {client.phone && (
                                <button onClick={(e) => {e.stopPropagation(); window.open('tel:' + client.phone);}}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 transition-colors"
                                  title="Κλήση">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                                  Κλήση
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="mb-2">
                            <Badge variant="outline">{client.activeCases} Υποθέσεις</Badge>
                          </div>
                          <p className="text-sm font-semibold text-green-600">
                            €{client.totalBilled.toLocaleString()} συνολικά
                          </p>
                          <div className="flex space-x-2 mt-3">
                            <Button size="sm" variant="outline">
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Other tabs to be continued... */}
          <TabsContent value="cases">
            {/* CASES TAB */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Διαχείριση Υποθέσεων</CardTitle>
                    <CardDescription>Όλες οι υποθέσεις με AI Success Prediction</CardDescription>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέα Υπόθεση
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cases.map(caseItem => (
                    <Card key={caseItem.id} className="border-l-4" style={{
                      borderLeftColor: 
                        caseItem.priority === 'high' ? '#ef4444' : 
                        caseItem.priority === 'medium' ? '#f59e0b' : '#10b981'
                    }}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge variant="outline">{caseItem.caseNumber}</Badge>
                              <Badge className={
                                caseItem.type === 'civil' ? 'bg-blue-500' :
                                caseItem.type === 'labor' ? 'bg-green-500' :
                                caseItem.type === 'commercial' ? 'bg-slate-700' :
                                'bg-gray-500'
                              }>
                                {caseItem.typeLabel}
                              </Badge>
                              {caseItem.status === 'active' && (
                                <Badge className="bg-green-600">Ενεργή</Badge>
                              )}
                            </div>
                            
                            <h3 className="text-lg font-bold mb-1">{caseItem.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">{caseItem.clientName}</p>
                            <p className="text-xs text-gray-500 mb-3">{caseItem.description}</p>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">Δικαστήριο</p>
                                <p className="font-medium">{caseItem.court}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Αρ. Δικογραφίας</p>
                                <p className="font-medium">{caseItem.courtFileNumber}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Επόμενη Συζήτηση</p>
                                <p className="font-medium">{new Date(caseItem.nextHearing).toLocaleDateString('el-GR')}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Αξία Διαφοράς</p>
                                <p className="font-medium">€{caseItem.estimatedValue.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="ml-6 text-right">
                            {/* AI Success Rate */}
                            <div className="mb-4">
                              <div className="flex items-center justify-end mb-1">
                                <Brain className="h-4 w-4 text-slate-700 mr-1" />
                                <span className="text-xs font-medium text-gray-600">AI Πρόβλεψη</span>
                              </div>
                              <div className="text-3xl font-bold" style={{
                                color: 
                                  caseItem.aiSuccessRate >= 75 ? '#10b981' :
                                  caseItem.aiSuccessRate >= 60 ? '#f59e0b' : '#ef4444'
                              }}>
                                {caseItem.aiSuccessRate}%
                              </div>
                              <p className="text-xs text-gray-500">Επιτυχία</p>
                            </div>
                            
                            <div className="flex space-x-2">
                              <Button size="sm" variant="outline" onClick={() => setSelectedCase(caseItem)}>
                                <Eye className="h-3 w-3 mr-1" />
                                Προβολή
                              </Button>
                              <Button size="sm" variant="outline">
                                <Search className="h-3 w-3 mr-1" />
                                Νομολογία
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DEADLINES TAB */}
          <TabsContent value="deadlines">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Προθεσμίες με AI Calculator</CardTitle>
                    <CardDescription>Αυτόματος υπολογισμός βάσει ΚΠολΔ</CardDescription>
                  </div>
                  <Button onClick={() => setShowDeadlineCalc(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέα Προθεσμία
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Critical Deadlines Alert */}
                <Alert className="mb-6 border-red-500 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <strong>Προσοχή!</strong> Έχετε {deadlines.filter(d => d.daysLeft <= 7).length} κρίσιμες προθεσμίες τις επόμενες 7 ημέρες
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  {deadlines.map(deadline => (
                    <div 
                      key={deadline.id} 
                      className={`border-l-4 p-4 rounded-lg ${
                        deadline.priority === 'critical' ? 'border-red-500 bg-red-50' :
                        deadline.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                        'border-yellow-500 bg-yellow-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-bold">{deadline.title}</h3>
                            {deadline.aiCalculated && (
                              <Badge variant="outline" className="text-slate-700 border-slate-700">
                                <Brain className="h-3 w-3 mr-1" />
                                AI
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{deadline.caseName}</p>
                          <p className="text-xs text-gray-600">{deadline.description}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Προθεσμία: {new Date(deadline.dueDate).toLocaleDateString('el-GR')}
                          </p>
                        </div>
                        
                        <div className="text-right ml-6">
                          <div className={`text-2xl font-bold mb-1 ${
                            deadline.daysLeft <= 3 ? 'text-red-600' :
                            deadline.daysLeft <= 7 ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            {deadline.daysLeft}
                          </div>
                          <p className="text-xs text-gray-600">ημέρες</p>
                          <Button size="sm" className="mt-3" variant="outline">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ολοκλήρωση
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Deadline with AI */}
                <Card className="mt-6 border-2 border-slate-200 bg-slate-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-3">
                      <Brain className="h-8 w-8 text-slate-700" />
                      <div>
                        <h4 className="font-bold text-slate-900">AI Deadline Calculator</h4>
                        <p className="text-sm text-slate-700">Αυτόματος υπολογισμός προθεσμιών ΚΠολΔ</p>
                      </div>
                      <Button className="ml-auto bg-slate-900 hover:bg-slate-800" onClick={() => setShowDeadlineCalc(true)}>
                        Υπολογισμός
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TIME TRACKING TAB */}
          <TabsContent value="time">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Καταγραφή Ωρών</CardTitle>
                    <CardDescription>Smart time tracking με AI suggestions</CardDescription>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέα Εγγραφή
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-blue-50">
                    <CardContent className="pt-6">
                      <p className="text-sm text-gray-600 mb-1">Συνολικές Ώρες</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {timeEntries.reduce((sum, t) => sum + t.hours, 0).toFixed(1)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-green-50">
                    <CardContent className="pt-6">
                      <p className="text-sm text-gray-600 mb-1">Χρεώσιμες</p>
                      <p className="text-3xl font-bold text-green-600">
                        {timeEntries.filter(t => t.billable).reduce((sum, t) => sum + t.hours, 0).toFixed(1)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-orange-50">
                    <CardContent className="pt-6">
                      <p className="text-sm text-gray-600 mb-1">Μη Τιμολογημένες</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {stats.unbilledHours.toFixed(1)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-50">
                    <CardContent className="pt-6">
                      <p className="text-sm text-gray-600 mb-1">Αξία</p>
                      <p className="text-3xl font-bold text-slate-700">
                        €{timeEntries.reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Time Entries Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Ημερομηνία</th>
                        <th className="text-left p-3 text-sm font-semibold">Υπόθεση</th>
                        <th className="text-left p-3 text-sm font-semibold">Περιγραφή</th>
                        <th className="text-right p-3 text-sm font-semibold">Ώρες</th>
                        <th className="text-right p-3 text-sm font-semibold">Τιμή/Ώρα</th>
                        <th className="text-right p-3 text-sm font-semibold">Σύνολο</th>
                        <th className="text-center p-3 text-sm font-semibold">Κατάσταση</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntries.map(entry => (
                        <tr key={entry.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{new Date(entry.date).toLocaleDateString('el-GR')}</td>
                          <td className="p-3 text-sm font-medium">{entry.caseName}</td>
                          <td className="p-3 text-sm">{entry.description}</td>
                          <td className="p-3 text-sm text-right font-medium">{entry.hours}</td>
                          <td className="p-3 text-sm text-right">€{entry.hourlyRate}</td>
                          <td className="p-3 text-sm text-right font-bold">€{entry.amount}</td>
                          <td className="p-3 text-center">
                            {entry.billable ? (
                              <Badge className="bg-green-500">Χρεώσιμη</Badge>
                            ) : (
                              <Badge variant="outline">Μη χρεώσιμη</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVOICES TAB */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Τιμολόγια & Χρεώσεις</CardTitle>
                    <CardDescription>Διαχείριση εισπράξεων</CardDescription>
                  </div>
                  <Button onClick={() => setShowInvoiceCreator(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέο Τιμολόγιο
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {invoices.map(invoice => (
                    <Card key={invoice.id} className={`border-l-4 ${
                      invoice.status === 'paid' ? 'border-green-500' : 'border-orange-500'
                    }`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-bold">{invoice.invoiceNumber}</h3>
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-500' : 'bg-orange-500'
                              }>
                                {invoice.status === 'paid' ? 'Πληρωμένο' : 'Εκκρεμεί'}
                              </Badge>
                            </div>
                            
                            <p className="text-sm font-medium mb-1">{invoice.clientName}</p>
                            <p className="text-xs text-gray-500 mb-3">
                              Έκδοση: {new Date(invoice.date).toLocaleDateString('el-GR')} • 
                              Λήξη: {new Date(invoice.dueDate).toLocaleDateString('el-GR')}
                            </p>
                            
                            <div className="text-sm text-gray-600">
                              {invoice.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between py-1">
                                  <span>{item.description}</span>
                                  <span className="font-medium">€{item.amount}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="ml-6 text-right">
                            <p className="text-sm text-gray-600 mb-1">Σύνολο</p>
                            <p className="text-3xl font-bold text-gray-900">
                              €{invoice.amount.toLocaleString()}
                            </p>
                            <div className="flex space-x-2 mt-4">
                              <Button size="sm" variant="outline">
                                <Download className="h-3 w-3 mr-1" />
                                PDF
                              </Button>
                              <Button size="sm" variant="outline">
                                <Mail className="h-3 w-3 mr-1" />
                                Email
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI INSIGHTS TAB */}
                    <TabsContent value="ai">
            <AIInsights />
          </TabsContent>
        
          {/* AADE TAB */}
          <TabsContent value="aade">
            <AADESettings 
              existingSettings={aadeSettings}
              onSave={(settings) => {
                setAadeSettings(settings);
                // TODO: Call API to save encrypted
                console.log('AADE Settings saved:', settings);
              }}
            />
          </TabsContent>

<TabsContent value="search">
            <LegalSearch />
          </TabsContent>
          <TabsContent value="documents">
            <DocGenerator />
          </TabsContent>
          <TabsContent value="conflicts">
            <ConflictCheck />
          </TabsContent>
          <TabsContent value="syntaxi">
            <Syntaxi token={localStorage.getItem('token') || localStorage.getItem('nomokratia_token')} />
          </TabsContent>
        </Tabs>
        {/* Pricing Section */}
        <div className="mt-8">
          <Card className="bg-slate-900 text-white border-0 mb-8">
            <CardContent className="py-8 text-center">
              <h3 className="text-3xl font-bold mb-2">Έτοιμοι για το καλύτερο Legal CRM;</h3>
              <p className="text-slate-300 text-lg">Ξεκλειδώστε όλες τις AI δυνατότητες • 3.5M νομικά έγγραφα • Unlimited χρήση</p>
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-gray-200 hover:border-slate-400 transition-colors">
              <CardContent className="pt-6 text-center">
                <h4 className="text-xl font-bold">Δοκιμαστικό</h4>
                <div className="text-4xl font-bold text-gray-900 mt-4">Δωρεάν</div>
                <p className="text-gray-500 text-sm mb-6">14 ημέρες πλήρης πρόσβαση</p>
                <div className="space-y-2 text-left text-sm mb-6">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Πλήρες CRM — Πελάτες, Υποθέσεις, Προθεσμίες</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Νομική Αναζήτηση σε 3.5M+ έγγραφα</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Ανάλυση Νομολογίας με 21 κανόνες</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />10 AI αναλύσεις / ημέρα</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Χρονοχρέωση & Καταγραφή Ωρών</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Εισαγωγή από CSV / Excel</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Χωρίς πιστωτική κάρτα</div>
                </div>
                <Button onClick={() => window.location.href = "/?register=true"} className="w-full" variant="outline">Ξεκινήστε Δωρεάν</Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-900 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2"><span className="bg-slate-900 text-white text-xs px-3 py-1 rounded-full">Δημοφιλές</span></div>
              <CardContent className="pt-6 text-center">
                <h4 className="text-xl font-bold">Επαγγελματικό</h4>
                <div className="text-4xl font-bold text-gray-900 mt-4">€199<span className="text-lg font-normal text-gray-500">/μήνα</span></div>
                <p className="text-gray-500 text-sm mb-6">Για δικηγόρους & ατομικά γραφεία</p>
                <div className="space-y-2 text-left text-sm mb-6">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Απεριόριστες AI Αναλύσεις Νομολογίας</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Δημιουργία 18 τύπων εγγράφων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Αγωγές, Εξώδικα, Προτάσεις, Συμβάσεις</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AADE / myDATA Τιμολόγηση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Έλεγχος Σύγκρουσης (Ν. 4194/2013)</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Χρονοχρέωση & Αυτόματη Τιμολόγηση</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Προθεσμίες με Υπενθυμίσεις</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />AI Insights & Στατιστικά Υποθέσεων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Email Υποστήριξη</div>
                </div>
                <Button onClick={() => window.location.href = "/?register=true"} className="w-full bg-slate-900 hover:bg-slate-800 text-white">Ξεκινήστε Τώρα</Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-slate-400 transition-colors">
              <CardContent className="pt-6 text-center">
                <h4 className="text-xl font-bold">Δικηγορικό Γραφείο</h4>
                <div className="text-4xl font-bold text-gray-900 mt-4">Κατόπιν επικ.</div>
                <p className="text-gray-500 text-sm mb-6">Για γραφεία με 2+ δικηγόρους</p>
                <div className="space-y-2 text-left text-sm mb-6">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Όλα τα Επαγγελματικού</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Έως 10 δικηγόροι / γραφείο</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Κεντρική Διαχείριση Υποθέσεων</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Ανάθεση Εργασιών & Ρόλοι</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Cloud Sync (Google Drive, OneDrive)</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />API Access & Custom Integrations</div>
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />Priority Support & Εκπαίδευση</div>
                </div>
                <Button onClick={() => window.location.href = "mailto:info@nomokratia.ai?subject=Εταιρικό Πλάνο"} className="w-full" variant="outline">Επικοινωνήστε</Button>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">Χωρίς δέσμευση • Ακύρωση ανά πάσα στιγμή • Άμεση ενεργοποίηση</p>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-white text-slate-800 py-10 px-4 mt-8 border-t border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" alt="ΝΟΜΟΚΡΑΤΙΑ Logo" className="h-12 w-auto mb-4" />
              <p className="text-slate-500 text-sm">Η επαγγελματική νομική πλατφόρμα σας για σύγχρονα δικηγορικά γραφεία.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-slate-800">Υποστήριξη</h4>
              <p className="text-slate-600 text-sm">📧 info@nomokratia.ai</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-slate-800">Εταιρεία</h4>
              <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                <span>🇪🇺 EU AI Pact Signatory</span>
                <span>🇪🇺 Registered EU Expert</span>
                <span>🔒 GDPR Compliant</span>
                <span>🇬🇷 Made in Greece</span>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-6 text-center text-slate-500 text-sm">
            <div className="flex justify-center gap-6 mb-3">
              <PrivacyPolicyModal />
              <TermsOfServiceModal />
              <a href="mailto:info@nomokratia.ai" className="text-slate-500 hover:text-slate-900 transition-colors">📧 Επικοινωνία</a>
            </div>
            <p>&copy; 2026 ΝΟΜΟΚΡΑΤΙΑ by PROTOS AI Agency IKE. Όλα τα δικαιώματα διατηρούνται.</p>
            <p className="text-slate-400 text-xs mt-2">PROTOS AI Agency IKE · Αρχαιολογικού Μουσείου 17, 54640 Θεσσαλονίκη · ΑΦΜ 802208607 · ΓΕΜΗ 172146906000</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const WrappedCRM = () => (
  <AuthProvider>
    <CRMProfessional />
  </AuthProvider>
);

export default WrappedCRM;